import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  finalize,
  firstValueFrom,
  lastValueFrom,
  map,
  Observable,
  startWith,
  Subject,
  takeUntil,
} from 'rxjs';
import { DialogEditOutflowObservationComponent } from 'src/app/almacen-insumos/egreso/dialog-edit-outflow-observation/dialog-edit-outflow-observation.component';
import { DialogFoodOutflowEvidenceComponent } from 'src/app/almacen-insumos/egreso/dialog-evidence/dialog-evidence.component';
import { type AdditionalOutflowsModel } from 'src/app/models/additional-outflows.model';
import { DishModel } from 'src/app/models/dish.model';
import { type FoodItemOutflowModel } from 'src/app/models/food-item-outflow.model';
import { type FoodItemModel } from 'src/app/models/food-item.model';
import { AyBService } from 'src/app/services/api/ayb.service';
import { ProductService } from 'src/app/services/api/product.service';
import { SalesService } from 'src/app/services/api/sales.service';
import { SourcingService } from 'src/app/services/api/sourcing.service';
import { FormCocinaService } from 'src/app/services/forms/form-cocina.service';
import { PdfGeneratorService } from 'src/app/services/pdf-generator.service';
import { NotificationService } from 'src/shared/notification.service';
import Swal from 'sweetalert2';
import {
  FileWithServicesAndMenu,
  FoodItemQuantityGroup,
  GroupedFoodItemOutflow,
  GroupedServiceOutflow,
  ProcessedFoodItem,
} from '../models';

@Component({
  selector: 'app-dialog-egreso-insumo',
  templateUrl: './dialog-egreso-insumo.component.html',
  styleUrls: ['./dialog-egreso-insumo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogEgresoInsumoComponent implements OnInit, OnDestroy {
  // Subject used to unsubscribe from all observables when the component is destroyed
  private _unsubscribeOnDestroy$: Subject<boolean> = new Subject<boolean>();

  // Stores the base64 URL or reference of the generated PDF document
  pdf: any;
  // Flag indicating if an operation (like generating PDF) was successful
  success = false;
  // Indicates whether the component is loading initial data from the backend
  isLoading = true;
  // Indicates if the PDF generation process is currently in progress
  isLoadingGenerarPdf = false;
  // Controls the visibility of the "Añadir Insumos" form section
  openMoreInformation = false;
  // Indicates if the confirmation process of selected food items is running
  loadingConfirmarInsumos = false;
  // Tracks whether the selected food items have been successfully confirmed
  isInsumosConfirmed = false;

  // Tracks if there have been any changes made that require updating
  isthereChanges = false;
  // Total number of menus for the current service, used to calculate quantities
  totalMenuCount = 0;
  // The name of the currently selected menu
  selectedMenu: string | null = null;
  // The currently selected service containing its details
  selectedService: GroupedServiceOutflow | null = null;

  // List of grouped food items (supplies) displayed and available for outflow
  insumos: GroupedFoodItemOutflow[] = [];
  // List of all food items available in the warehouse
  warehouseSupplies: FoodItemModel[] = [];
  // List of unique categories available for filtering
  availableCategories: string[] = [];
  // The currently selected category filter for the table
  selectedCategoria: string | null = null;

  // List of existing food item outflows registered for the current service
  outflowsByService: FoodItemOutflowModel[] = [];
  // Temporarily stores the new total quantity being edited in the table row
  totalQuantityToRemove = 0;

  // Index of the currently active or editable quantity field in the table
  activeTextArea: string | null = null;
  // Defines the order and list of columns displayed in the Material Table
  displayedColumns = [
    'Check',
    'codigoInsumo', // Displayed as Categoria
    'nombreInsumo', // Displayed as Insumo
    'cantidad',
    'totalMenuCount', // Displayed as Total Menús
    'cantidadTotal',
    'unidadMedida', // Displayed as Unidad de Medida
    'observation', // Displayed as Observación
    'evidencia',
  ];
  // Reactive form group for adding a new additional food item outflow
  newInsumoForm;
  // Form control for the autocomplete input to search for a food item (SKU)
  inputInsumoControl = new FormControl<string | any>('');
  // List of food items required by the dishes in the selected menu
  foodItemsFromDishes: GroupedFoodItemOutflow[] = [];
  // Observable providing filtered warehouse food items for the autocomplete
  autocompleteFoodItem$!: Observable<FoodItemModel[]>;
  // Observable providing filtered autocomplete options with duplicate validation status
  filteredOptionsWithValidation$!: Observable<
    { option: FoodItemModel; isDuplicate: boolean }[]
  >;
  // The data source for the Material Table containing the grouped food items
  dataSource: MatTableDataSource<GroupedFoodItemOutflow> =
    new MatTableDataSource<GroupedFoodItemOutflow>([]);

  // Reference to the Material Paginator for table pagination
  paginator!: MatPaginator;
  // Reference to the Material Sort for table sorting
  sort!: MatSort;

  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.dataSource.paginator = this.paginator;
  }
  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.sort = ms;
    this.dataSource.sort = this.sort;
  }

  constructor(
    private readonly cdRef: ChangeDetectorRef,
    private readonly productService: ProductService,
    private readonly formCocinaService: FormCocinaService,
    private readonly pdfGeneratorService: PdfGeneratorService,
    private readonly notificationService: NotificationService,
    private readonly aybService: AyBService,
    private readonly sourcingService: SourcingService,
    private readonly salesService: SalesService,
    public dialogRef: MatDialogRef<DialogEgresoInsumoComponent>,
    public matDialogHandler: MatDialog,
    @Inject(MAT_DIALOG_DATA)
    public dialogData: {
      EDIT_FILE_OUTFLOWS: {
        file: FileWithServicesAndMenu;
        currentService?: GroupedServiceOutflow | null;
        insumos?: GroupedFoodItemOutflow[] | null;
      };
      EDIT_ADDITIONAL_OUTFLOWS: {
        additionalOutflow: AdditionalOutflowsModel;
      };
      CREATE_ADDITIONAL_OUTFLOWS: {};
    },
  ) {
    this.newInsumoForm = this.formCocinaService.getInsumoForm();

    this.totalMenuCount =
      this.dialogData?.EDIT_FILE_OUTFLOWS.currentService?.totalMenuXFilaAYB ||
      0;
    this.selectedMenu =
      this.dialogData?.EDIT_FILE_OUTFLOWS.currentService?.menu?.nombreMenu ||
      null;
    this.selectedService =
      this.dialogData?.EDIT_FILE_OUTFLOWS.currentService || null;
  }

  ngOnInit(): void {
    this.aybService
      .queryGetMenuById({
        menuId:
          this.dialogData.EDIT_FILE_OUTFLOWS.currentService?.menu?.id ?? '',
      })
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe({
        next: async ({ getMenuById }) => {
          try {
            // Step 1: Get food items from menu dishes
            // Step 2: Get warehouse supplies
            // Step 3: Get existing outflows for this service
            // NOTE: Existing outflows take priority over calculated quantities from dishes
            const [foodItemsFromDishes, warehouseSupplies, existingOutflows] =
              await Promise.all([
                this._extractFoodItemsFromDishes(getMenuById?.platos || []),
                this._getWarehouseSupplies(),
                this._getExistingOutflowsByMenuByServiceByFile(),
              ]);

            this.foodItemsFromDishes = foodItemsFromDishes;
            this.warehouseSupplies = warehouseSupplies;
            // this.warehouseSupplies = warehouseSupplies.filter(
            //   warehouseFoodItem => {
            //     return foodItemsFromDishes.every(
            //       foodItem => foodItem.id !== warehouseFoodItem.id
            //     )
            //   }
            // )
            // Step 4: Process removable food items from existing outflows
            // Uses outflow quantities directly as cantidadTotal
            const removableFoodItems = this._outflowsThatCanBeRemoved({
              serviceExistingOutflows: existingOutflows,
              foodItemsFromDishes,
            });

            // Step 5: Merge food items and group by quantity
            // Sums repeated food items by quantity, preserving outflow quantities
            const mergedFoodItems = this._mergeFoodItems({
              foodItemsFromDishes,
              removableFoodItems,
            });

            const groupedFoodItems = this._groupFoodItemsByQuantity({
              foodItems: mergedFoodItems,
            });

            // Step 6: Add additional food supplies from dialog data
            this._addAdditionalFoodSupplies({ groupedFoodItems });

            // Step 7: Calculate total quantities
            // Prioritizes existing outflow quantities over calculated quantities
            const processedFoodItems = await this._calculateTotalQuantities({
              groupedFoodItems,
            });

            // Step 8: Apply existing outflow data to food items
            this._applyExistingOutflowData({
              existingOutflows,
              processedFoodItems,
            });

            // Step 9: Sort and filter
            const sortedFoodItems = this._sortFoodItems({
              foodItems: processedFoodItems,
            });
            // this._filterWarehouseSupplies(sortedFoodItems)

            // Step 10: Update component state
            this._updateComponentState({
              sortedFoodItems,
            });
          } catch (error) {
            console.error('Error processing food items:', error);
          } finally {
            this.isLoading = false;
            this.cdRef.markForCheck();
          }
        },
        error: (e) => {
          console.error('Error getting menu:', e);
          this.isLoading = false;
          this.cdRef.markForCheck();
        },
      });
    this.autocompleteFoodItem$ = this.inputInsumoControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        const searchTerm =
          typeof value === 'string' ? value : value?.nombreInsumo || '';
        return searchTerm
          ? this._autocompleteFilterWarehouseFoodItems(searchTerm)
          : this.warehouseSupplies.slice();
      }),
    );

    this._initializeFilteredOptions();

    this.autocompleteFoodItem$.subscribe((filteredItems) => { });
    this.cdRef.markForCheck();
  }
  ngOnDestroy(): void {
    this._unsubscribeOnDestroy$.next(true);
    this.onCloseDialog();
  }

  handleInformation(open: boolean) {
    this.openMoreInformation = open;
  }

  openDialogEditObservation(insumo: GroupedFoodItemOutflow) {
    const dialogConfig = new MatDialogConfig();

    const foodOutflow = this.outflowsByService.find(
      (serviceOutflow) => serviceOutflow.insumoId === insumo.id,
    );

    if (foodOutflow == null) {
      this.notificationService.notifyWarning(
        `Primero debe confirmar el insumo: ${insumo.nombreInsumo}`,
      );
      return;
    }

    dialogConfig.data = {
      prevObservation: insumo.observation,
      egresoInsumo: foodOutflow,
    };

    dialogConfig.width = '390px';
    dialogConfig.minHeight = '226px';
    dialogConfig.autoFocus = false;
    const dialogRef = this.matDialogHandler.open(
      DialogEditOutflowObservationComponent,
      dialogConfig,
    );

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe((result) => {
        if (!result?.editedOutflow) {
          return;
        }
        const updatedEgreso = result.editedOutflow;

        const warehouseIndex = this.insumos.findIndex(
          (item) => item.id === insumo.id,
        );

        if (warehouseIndex !== -1) {
          this.insumos[warehouseIndex].observation = updatedEgreso.observation;
        }

        const dataSourceIndex = this.dataSource.data.findIndex(
          (item) => item.id === insumo.id,
        );

        if (dataSourceIndex !== -1) {
          this.dataSource.data[dataSourceIndex].observation =
            updatedEgreso.observation;
          this.dataSource.data = [...this.dataSource.data];
        }

        this.notificationService.showSuccessAlert(
          'Observación actualizada exitosamente',
        );
        this.cdRef.markForCheck();
      });
  }
  openDialogUploadEvidence(insumo: GroupedFoodItemOutflow) {
    const selectedOutflow = this.outflowsByService.find(
      (serviceOutflow) => serviceOutflow.insumoId === insumo.id,
    );
    if (selectedOutflow == null) {
      this.notificationService.notifyWarning(
        `Primero debe confirmar el insumo: ${insumo.nombreInsumo}`,
      );
      return;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      outflow: selectedOutflow,
    };
    let outflowEvidenceDialogRef = this.matDialogHandler.open(
      DialogFoodOutflowEvidenceComponent,
      dialogConfig,
    );

    outflowEvidenceDialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe((result: { updatedOutflow: FoodItemOutflowModel }) => {
        if (
          !result ||
          !result?.updatedOutflow ||
          result.updatedOutflow.hasDocument === insumo.hasDocument
        ) {
          return;
        }

        const index = this.outflowsByService.findIndex(
          (egreso) => egreso.id === result.updatedOutflow.id,
        );
        if (index !== -1) {
          this.outflowsByService[index] = result.updatedOutflow;
        }

        const updatedEgreso = result.updatedOutflow;

        const warehouseIndex = this.insumos.findIndex(
          (item) => item.id === insumo.id,
        );

        if (warehouseIndex !== -1) {
          this.insumos[warehouseIndex].hasDocument =
            !!updatedEgreso?.hasDocument;
        }

        const dataSourceIndex = this.dataSource.data.findIndex(
          (item) => item.id === insumo.id,
        );

        if (dataSourceIndex !== -1) {
          this.dataSource.data[dataSourceIndex].hasDocument =
            !!updatedEgreso?.hasDocument;
        }
      });
  }

  private _autocompleteFilterWarehouseFoodItems(searchTerm: string) {
    const filterValue = searchTerm.toLowerCase();
    if (!searchTerm) return this.warehouseSupplies;

    return this.warehouseSupplies.filter((sku) =>
      sku?.nombreInsumo?.toLowerCase().includes(filterValue),
    );
  }

  private _initializeFilteredOptions(): void {
    this.filteredOptionsWithValidation$ =
      this.inputInsumoControl.valueChanges.pipe(
        startWith(''),
        map((value) => {
          const searchTerm =
            typeof value === 'string' ? value : value?.nombreInsumo || '';
          const filteredFoodItems = searchTerm
            ? this._autocompleteFilterWarehouseFoodItems(searchTerm)
            : this.warehouseSupplies.slice();

          return filteredFoodItems.map((foodItem) => ({
            option: foodItem,
            isDuplicate: this.foodItemsFromDishes.some(
              (foodItemFromDish) => foodItemFromDish.id === foodItem.id,
            ),
          }));
        }),
      );
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  async getProductName(productId: string) {
    if (productId) {
      const product = await lastValueFrom(
        this.productService.queryGetProductById({ productId }),
      );
      return product.getProductById.nombre;
    } else return 'Servicio Adicional';
  }

  async getCocineros() {
    const servicesPC = (
      await lastValueFrom(
        this.salesService.getValidServicesFilter(
          this.dialogData.EDIT_FILE_OUTFLOWS.file.id,
          'PERSONALDECAMPO',
        ),
      )
    ).GetValidServicesFilter;
    const cocineros = servicesPC.filter(
      (service: any) => service.categoriaServicio === 'COCINERO',
    );

    const providerIds = Array.from(
      new Set(cocineros.map((cocinero: any) => cocinero.providerId)),
    );

    const namesCocineros: any[] = [];
    await Promise.all(
      providerIds.map(async (provider: any) => {
        const prov = (
          await lastValueFrom(this.sourcingService.queryGetProvider(provider))
        ).getProvider;
        namesCocineros.push(prov.nombre);
      }),
    );

    return namesCocineros;
  }

  async generarPDF() {
    if (!this.isLoadingGenerarPdf) {
      if (
        this.dialogData.EDIT_FILE_OUTFLOWS.currentService &&
        this.selectedMenu
      ) {
        this.isLoadingGenerarPdf = true;
        const productName = await this.getProductName(
          this.dataSource.data[0]?.service?.productId ?? '',
        );

        const cocineros = await this.getCocineros();
        const insumosYcostos = await Promise.all(
          this.dataSource.data
            .filter((insumo) => insumo.checked)
            .map(async (insumo) => {
              try {
                const res = await lastValueFrom(
                  this.aybService.queryGetInsumoCosto({
                    insumoId: insumo.id || '',
                  }),
                );
                const costo = res.getInsumoCosto;
                const costoTotal = Number(costo) * Number(insumo.cantidadTotal);
                return {
                  ...insumo,
                  costo,
                  costoTotal: Number.isInteger(costoTotal)
                    ? costoTotal
                    : parseFloat(costoTotal.toFixed(1)),
                };
              } catch (error) {
                console.error(
                  `Error obteniendo costo para insumo ${insumo.id}`,
                  error,
                );
                return {
                  ...insumo,
                  costo: 0,
                  costoTotal: 0,
                };
              }
            }),
        );

        try {
          this.pdf = await this.pdfGeneratorService.generatePdf({
            content: insumosYcostos,
            headers: [
              'Insumo',
              'Unidad de Medida',
              'Cantidad Total',
              'Costo U.',
              'Costo Total',
            ],
            metadata: {
              fileNumber:
                this.dialogData.EDIT_FILE_OUTFLOWS.file.fileNumber || undefined,
              dateRange:
                this.dialogData.EDIT_FILE_OUTFLOWS.file.dateRange || undefined,
              lstPax:
                this.dialogData.EDIT_FILE_OUTFLOWS.file.lstPaX || undefined,
              productName,
              cocineros,
              serviceId:
                this.dialogData.EDIT_FILE_OUTFLOWS.currentService.id || '',
              serviceName:
                this.dialogData.EDIT_FILE_OUTFLOWS.currentService
                  .nombreServicio || '',
              totalMenuXFilaAYB:
                this.dialogData.EDIT_FILE_OUTFLOWS.currentService
                  ?.totalMenuXFilaAYB || 0,
              menu: this.selectedMenu || undefined,
            },
          });
          this.updateItineraryPdf(true);
        } catch (error) {
          this.success = false;
        } finally {
          this.isLoadingGenerarPdf = false;
          this.cdRef.markForCheck();
        }
        this.isthereChanges = true;
      } else {
        Swal.fire({
          toast: true,
          position: 'top-end',
          title: 'Debe seleccionar un servicio y un menú',
          icon: 'warning',
          showConfirmButton: false,
          timer: 3000,
        });
      }
    }
  }

  getOptionText(option: any) {
    return option.nombreInsumo;
  }

  updateUnidadMedida(event: MatAutocompleteSelectedEvent) {
    this.newInsumoForm
      .get(['unidadMedida'])
      ?.setValue(event.option.value.unidadMedida);
  }
  addFoodItemToTheFileAsAdditionalFoodItem() {
    if (this.dialogData.EDIT_FILE_OUTFLOWS.currentService) {
      if (
        !!this.inputInsumoControl.value &&
        !!this.newInsumoForm.value.unidadMedida &&
        this.newInsumoForm.value.cantidad
      ) {
        const existingFoodItem = this.dataSource.data.find(
          (item) => item.id === this.inputInsumoControl.value.id,
        );

        const existingInsumo = this.insumos.find(
          (item) => item.id === this.inputInsumoControl.value.id,
        );

        if (existingFoodItem || existingInsumo) {
          this.notificationService.notifyWarning(
            `Este insumo ya existe en la lista: ${this.inputInsumoControl.value.nombreInsumo}`,
          );
          return;
        }

        const initialNewFoodOutflow = {
          id: this.inputInsumoControl.value.id,
          nombreInsumo: this.inputInsumoControl.value.nombreInsumo,
          unidadMedida: this.newInsumoForm.value.unidadMedida,
          cantidad: this.newInsumoForm.value.cantidad
            ? Number(this.newInsumoForm.value.cantidad)
            : 0,
          menu: { nombreMenu: this.selectedMenu },
          service: {
            nombreServicio:
              this.dialogData.EDIT_FILE_OUTFLOWS.currentService.nombreServicio,
          },
        } as GroupedFoodItemOutflow;

        Swal.fire({
          title: 'Cargando egreso adicional...',
          text: `Por favor espere mientras se carga el egreso adicional para "${this.inputInsumoControl.value.nombreInsumo}". Con Cantidad Retirada: "${this.newInsumoForm.value.cantidad}" ${this.newInsumoForm.value.unidadMedida} del servicio "${this.dialogData.EDIT_FILE_OUTFLOWS.currentService.nombreServicio}" y menú "${this.selectedMenu}".`,
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        this.salesService
          .updateInsumosAdicionales(
            this.dialogData.EDIT_FILE_OUTFLOWS?.file?.id ?? '',
            initialNewFoodOutflow,
          )
          .pipe(
            takeUntil(this._unsubscribeOnDestroy$),
            finalize(() => {
              Swal.close();
            }),
          )
          .subscribe({
            next: (res) => {
              if (this.dialogData.EDIT_FILE_OUTFLOWS.insumos) {
                this.dialogData.EDIT_FILE_OUTFLOWS.insumos.push(
                  initialNewFoodOutflow,
                );
              } else {
                this.dialogData.EDIT_FILE_OUTFLOWS.insumos = [
                  initialNewFoodOutflow,
                ];
              }
              const foodOutflows = this.dataSource.data;
              const newFoodOutflow = {
                ...initialNewFoodOutflow,
                cantidadTotal:
                  this.dialogData.EDIT_FILE_OUTFLOWS.currentService &&
                    this.selectedMenu
                    ? (initialNewFoodOutflow?.cantidad ??
                      0 * this.totalMenuCount)
                    : (initialNewFoodOutflow?.cantidad ??
                      0 *
                      this.dialogData.EDIT_FILE_OUTFLOWS.file.lstPaX.length),
              };
              foodOutflows.unshift(newFoodOutflow);
              this.insumos.unshift(newFoodOutflow);
              this.dataSource.data = [...foodOutflows];
              if (this.dialogData.EDIT_FILE_OUTFLOWS.insumos?.length) {
                this.dialogData.EDIT_FILE_OUTFLOWS.insumos.push(
                  initialNewFoodOutflow,
                );
              } else {
                this.dialogData.EDIT_FILE_OUTFLOWS.insumos = [
                  initialNewFoodOutflow,
                ];
              }

              this._initializeFilteredOptions();
            },
          });

        this.newInsumoForm.reset();
        this.inputInsumoControl.reset();
      } else {
        this.notificationService.notifyWarning(
          'Falta ingresar campos requeridos',
        );
      }
    } else {
      this.notificationService.notifyWarning(
        'Debe seleccionar un servicio para agregar un insumo',
      );
    }
  }
  toggleAllRows() {
    if (this.isAllSelected()) {
      this.dataSource.data.map((fila) => {
        fila.checked = false;
      });
      this.insumos.map((fila) => {
        fila.checked = false;
      });
    } else {
      this.dataSource.data.map((fila) => {
        fila.checked = true;
      });
      this.insumos.map((fila) => {
        fila.checked = true;
      });
    }
    this.cdRef.markForCheck();
  }

  isAllSelected() {
    const selectedFoodSupplies = this.insumos.filter(
      (foodSupply) => foodSupply.checked == true,
    );
    const countOfSelectedSupplies = selectedFoodSupplies.length;
    const totalFoodSupplies = this.insumos.length;
    return countOfSelectedSupplies === totalFoodSupplies;
  }

  isAnySelected() {
    const selectedFoodSupplies = this.insumos.filter(
      (foodSupply) => foodSupply.checked == true,
    );
    return selectedFoodSupplies.length > 0;
  }

  hasPendingChanges(): boolean {
    if (!this.dialogData.EDIT_FILE_OUTFLOWS.currentService || !this.selectedMenu) {
      return false;
    }

    const foodItems = this.dataSource.data.filter((foodItem) => foodItem.checked);
    const checkedInsumos = this.insumos.filter((foodItem) => foodItem.checked);

    const newOutflows = foodItems.filter(
      (newInflowItem) =>
        !this.outflowsByService.find(
          (previousOutflow) =>
            previousOutflow.insumoId === newInflowItem.id &&
            previousOutflow.serviceName === this.dialogData.EDIT_FILE_OUTFLOWS.currentService?.nombreServicio &&
            previousOutflow.menuName === this.selectedMenu
        ),
    );

    const deletedOutflows = this.outflowsByService.filter(
      (currentOutflow) =>
        !checkedInsumos.find(
          (insumo) => insumo.id === currentOutflow.insumoId,
        ),
    );

    return newOutflows.length > 0 || deletedOutflows.length > 0;
  }

  isConfirmedOutflow(insumo: GroupedFoodItemOutflow): boolean {
    return this.outflowsByService.some(
      (outflow) => outflow.insumoId === insumo.id
    );
  }
  updateCheck(foodSupply: GroupedFoodItemOutflow) {
    foodSupply.checked = !foodSupply.checked;
    this.cdRef.markForCheck();
  }

  async updateOutflows() {
    if (this.loadingConfirmarInsumos) {
      return;
    }

    if (
      !this.dialogData.EDIT_FILE_OUTFLOWS.currentService ||
      !this.selectedMenu
    ) {
      this.notificationService.notifyWarning(
        'Por favor seleccione un servicio y menú',
      );
      return;
    }

    this.loadingConfirmarInsumos = true;
    const foodItems = this.dataSource.data.filter(
      (foodItem) => foodItem.checked,
    );
    const checkedInsumos = this.insumos.filter((foodItem) => foodItem.checked);

    const confirmedOutflows: FoodItemOutflowModel[] = [];

    foodItems.map((foodItem) => {
      confirmedOutflows.push({
        fileId: this.dialogData.EDIT_FILE_OUTFLOWS.file.id,
        insumoId: foodItem.id,
        cantidad: foodItem.cantidadTotal,
        nombre: foodItem.nombreInsumo,
        unidadMedida: foodItem.unidadMedida,
        serviceName:
          this.dialogData.EDIT_FILE_OUTFLOWS.currentService?.nombreServicio,
        menuName: this.selectedMenu || '',
        IdService: this.dialogData.EDIT_FILE_OUTFLOWS.currentService?.id,
        productId:
          this.dialogData.EDIT_FILE_OUTFLOWS.currentService?.objProducto?.id,
        fechaServicio:
          this.dialogData.EDIT_FILE_OUTFLOWS.currentService?.fechaServicio,
        day: this.dialogData.EDIT_FILE_OUTFLOWS.currentService?.day
          ? String(this.dialogData.EDIT_FILE_OUTFLOWS.currentService?.day)
          : '0',
        totalChoferAYB: this.dialogData.EDIT_FILE_OUTFLOWS.currentService
          ?.totalChoferAYB
          ? String(
            this.dialogData.EDIT_FILE_OUTFLOWS.currentService?.totalChoferAYB,
          )
          : '0',
        totalGuiaAYB: this.dialogData.EDIT_FILE_OUTFLOWS.currentService
          ?.totalGuiaAYB
          ? String(
            this.dialogData.EDIT_FILE_OUTFLOWS.currentService?.totalGuiaAYB,
          )
          : '0',
        totalMenuXFilaAYB: this.dialogData.EDIT_FILE_OUTFLOWS.currentService
          ?.totalMenuXFilaAYB
          ? String(
            this.dialogData.EDIT_FILE_OUTFLOWS.currentService
              ?.totalMenuXFilaAYB,
          )
          : '0',
        totalesPersonalCampoAYB: this.dialogData.EDIT_FILE_OUTFLOWS
          .currentService?.totalesPersonalCampoAYB
          ? String(
            this.dialogData.EDIT_FILE_OUTFLOWS.currentService
              ?.totalesPersonalCampoAYB,
          )
          : '0',
        totalPAXAYB: this.dialogData.EDIT_FILE_OUTFLOWS.currentService?.cantPAX
          ? String(this.dialogData.EDIT_FILE_OUTFLOWS.currentService?.cantPAX)
          : '0',
        totalTCAYB: this.dialogData.EDIT_FILE_OUTFLOWS.currentService
          ?.totalTCAYB
          ? String(this.dialogData.EDIT_FILE_OUTFLOWS.currentService?.tc)
          : '0',
        hasDocument: foodItem.hasDocument || false,
      });
    });
    const selectedNewOutflow = confirmedOutflows.filter(
      (newInflowItem) =>
        !this.outflowsByService.find(
          (previousOutflow) =>
            previousOutflow.insumoId === newInflowItem.insumoId &&
            previousOutflow.serviceName === newInflowItem.serviceName &&
            previousOutflow.menuName === newInflowItem.menuName,
        ),
    );

    const unselectedOutflows = this.outflowsByService
      .filter(
        (currentOutflow) =>
          !checkedInsumos.find(
            (insumo) => insumo.id === currentOutflow.insumoId,
          ),
      )
      .map((inflow) => {
        const insumo = this.insumos.find(
          (insumo) => insumo.id === inflow.insumoId,
        );

        return {
          ...inflow,
          cantidad: insumo?.cantidadTotal ?? 0,
        };
      }) as FoodItemOutflowModel[];

    const processedResponses: Array<
      | boolean
      | {
        message: string;
        success: boolean;
      }
    > = [];

    try {
      // Refrescar los egresos desde la base de datos para asegurar que no han sido creados por otra sesión
      await this._getExistingOutflowsByMenuByServiceByFile();
      
      const duplicateNewOutflow = selectedNewOutflow.find((newOutflow) =>
        this.outflowsByService.find(
          (existing) =>
            existing.insumoId === newOutflow.insumoId &&
            existing.serviceName === newOutflow.serviceName &&
            existing.menuName === newOutflow.menuName &&
            (existing.day || '0') === (newOutflow.day || '0')
        )
      );

      if (duplicateNewOutflow) {
        Swal.fire({
          title: 'Egresos ya generados',
          text: `Error, egresos ya fueron generados para ${duplicateNewOutflow.nombre}. Por favor, cierra y vuelva a abrir el modal. y verifique nuevamente este insumo a egresar.`,
          icon: 'warning',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Entendido'
        });
        this.loadingConfirmarInsumos = false;
        return;
      }
    } catch (e) {
      console.error('Error verificando egresos duplicados antes de actualizar:', e);
    }

    const totalToUpdate = unselectedOutflows.length + selectedNewOutflow.length;
    let successfulUpdates = 0;
    let htmlSummary = '';

    if (totalToUpdate > 0) {
      const getNewItemsHtml = () => {
        if (selectedNewOutflow.length === 0) return '';
        return `
          <p style="margin-bottom: 4px; font-size: 13px;"><strong>Nuevos Egresos (0 &rarr; Nueva Cant. Retirada):</strong></p>
          <ul style="margin-top: 0; font-size: 13px; color: #555;">
            ${selectedNewOutflow.map(o => `<li>${o.nombre} (0 &rarr; ${o.cantidad} ${o.unidadMedida})</li>`).join('')}
          </ul>
        `;
      };

      const getDeletedItemsHtml = () => {
        if (unselectedOutflows.length === 0) return '';
        return `
          <p style="margin-bottom: 4px; font-size: 13px;"><strong>Egresos Eliminados (Cant. Anterior &rarr; 0):</strong></p>
          <ul style="margin-top: 0; font-size: 13px; color: #555;">
            ${unselectedOutflows.map(o => `<li>${o.nombre} (${o.cantidad} &rarr; 0 ${o.unidadMedida})</li>`).join('')}
          </ul>
        `;
      };

      htmlSummary = `
        <div style="text-align: left; max-height: 250px; overflow-y: auto; margin-bottom: 15px;">
          ${getNewItemsHtml()}
          ${getDeletedItemsHtml()}
        </div>
      `;

      Swal.fire({
        title: 'Actualizando Egresos...',
        html: `
          ${htmlSummary}
          <hr>
          <p class="progress-text" style="font-weight: 600; text-align: center; color: #1976d2; margin-top: 10px; font-size: 15px;">
            Progreso: 0 / ${totalToUpdate} actualizados.
          </p>
        `,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
    }

    const updateProgress = () => {
      successfulUpdates++;
      const htmlContainer = Swal.getHtmlContainer();
      if (htmlContainer) {
        const progressElement = htmlContainer.querySelector('.progress-text');
        if (progressElement) {
          progressElement.innerHTML = `Progreso: ${successfulUpdates} / ${totalToUpdate} actualizados.`;
        }
      }
    };

    if (unselectedOutflows.length > 0) {
      await Promise.all(
        unselectedOutflows.map(async (outflowToRemove) => {
          try {
            const currentOutflowItem = this.outflowsByService.find(
              (currentOutflow) =>
                currentOutflow.insumoId === outflowToRemove.insumoId,
            );

            if (!currentOutflowItem) {
              console.warn(
                `No se encontró el egreso correspondiente al insumo ${outflowToRemove.insumoId}`,
              );
              return;
            }

            const deletedOutflowRes = await lastValueFrom(
              this.aybService.mutationDeleteEgresoInsumo({
                outflowId: currentOutflowItem?.id ?? '',
              }),
            );
            processedResponses.push(
              deletedOutflowRes?.mutationDeleteFoodOutflow != null,
            );

            processedResponses.push(
              deletedOutflowRes.mutationDeleteFoodOutflow,
            );
          } catch (error) {
            console.error(error);
            processedResponses.push(false);
          } finally {
            updateProgress();
          }
        }),
      );
    }
    if (selectedNewOutflow.length > 0) {
      await Promise.all(
        selectedNewOutflow.map(async (newOutflow) => {
          try {
            const createdFoodOutflowRes = await lastValueFrom(
              this.aybService.mutationCreateFoodItemOutflow(newOutflow),
            );

            processedResponses.push(
              createdFoodOutflowRes?.createEgresoInsumo != null,
            );

            processedResponses.push(!!createdFoodOutflowRes.createEgresoInsumo);
          } catch (error) {
            console.error(error);
            processedResponses.push(false);
          } finally {
            updateProgress();
          }
        }),
      );
    }

    try {
      await this._getExistingOutflowsByMenuByServiceByFile();
    } catch (e) {
      console.error('Error refreshing existing outflows:', e);
    }

    if (totalToUpdate > 0) {
      Swal.close();
    }

    this.isthereChanges = true;
    this.confirmInsumos(processedResponses, htmlSummary);
  }

  confirmInsumos(
    processedResponses: Array<
      | boolean
      | {
        message: string;
        success: boolean;
      }
    >,
    htmlSummary?: string,
  ) {
    this.isInsumosConfirmed = true;

    this.salesService
      .updateServiceItinerary({
        id:
          this.dialogData.EDIT_FILE_OUTFLOWS.currentService?.id ?? 'NO HAY ID',
        confirmInsumosAYB: this.isInsumosConfirmed,
      })
      .pipe(
        takeUntil(this._unsubscribeOnDestroy$),
        finalize(() => {
          this.loadingConfirmarInsumos = false;
          this.cdRef.markForCheck();
        }),
      )
      .subscribe({
        next: (updateServiceItineraryResponse) => {
          this.success = true;

          if (
            processedResponses.every(
              (res) =>
                // @ts-ignore
                res === true || (res?.success != null && res?.success === true),
            )
          ) {
            if (htmlSummary) {
              Swal.fire({
                title: 'Se confirmó los insumos exitosamente.',
                html: htmlSummary,
                icon: 'success',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Aceptar',
              });
            } else {
              Swal.fire({
                title: 'Sin cambios',
                text: 'No se encontraron insumos nuevos ni eliminados para actualizar.',
                icon: 'info',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Aceptar',
              });
            }
          } else {
            let notificationMessage = '';

            const responsesMessages = processedResponses.map(
              // @ts-ignore
              (res) => res?.message && res?.success,
            );

            for (const r of responsesMessages) {
              if (r.message && r.success === false) {
                notificationMessage += r.message;
              }
            }
            this.notificationService.notifyWarning(notificationMessage);
          }
        },
        error: (error: any) => {
          this.success = false;
          this.notificationService.notifyError(
            'Error al confirmar Insumos. Por favor, intente nuevamente o contacte al administrador.',
          );
        },
      });
  }
  async updateItineraryPdf(confirmPDFAYB: boolean) {
    this.isLoadingGenerarPdf = true;
    this.cdRef.markForCheck();

    this.salesService
      .updateServiceItinerary({
        id: this.dialogData.EDIT_FILE_OUTFLOWS.currentService?.id ?? '',
        confirmPDFAYB,
      })
      .pipe(takeUntil(this._unsubscribeOnDestroy$))
      .subscribe({
        next: (updateServiceItineraryResponse) => {
          this.success = true;
        },
        error: (error) => {
          this.success = false;
          this.notificationService.notifyError(error.message);
        },
      });
    this.isLoadingGenerarPdf = false;
    this.cdRef.markForCheck();
  }

  applyFilterSelect() {
    let dataNew = this.insumos;
    if (this.dialogData.EDIT_FILE_OUTFLOWS.currentService) {
      dataNew = dataNew?.filter(
        (item) =>
          item.service?.nombreServicio ===
          this.dialogData.EDIT_FILE_OUTFLOWS.currentService?.nombreServicio,
      );
    }

    if (this.selectedMenu) {
      dataNew = dataNew?.filter(
        (item) => item?.nombreMenu === this.selectedMenu,
      );
    }

    if (this.selectedCategoria) {
      dataNew = dataNew.filter((insumo) => {
        const codigoInsumo = insumo.codigoInsumo || '';
        return codigoInsumo.startsWith(this.selectedCategoria!);
      });
    }

    this.dataSource.data = dataNew;
  }

  updateFoodOutflowTotalQuantityToRemove(rowInsumo: GroupedFoodItemOutflow) {
    this.activeTextArea = null;

    if (rowInsumo.cantidadTotal === this.totalQuantityToRemove) {
      this.notificationService.notifyWarning(
        'La cantidad total ya está actualizada',
      );
      return;
    }

    const selectedInsumo = this.dialogData.EDIT_FILE_OUTFLOWS.insumos?.find(
      (insumo) => insumo.id === rowInsumo.id,
    );

    const foodOutflowToUpdate = {
      id: rowInsumo.id,
      nombreInsumo: rowInsumo.nombreInsumo,
      unidadMedida: rowInsumo.unidadMedida,
      menu: { nombreMenu: this.selectedMenu },
      cantidad: this.dialogData.EDIT_FILE_OUTFLOWS?.file?.cantPersonasAB
        ? Number(this.totalQuantityToRemove) /
        this.dialogData.EDIT_FILE_OUTFLOWS?.file?.cantPersonasAB
        : Number(this.totalQuantityToRemove) /
        (this.dialogData.EDIT_FILE_OUTFLOWS?.file?.lstPaX?.length || 1),
    };

    const isConfirmed = this.outflowsByService.some(
      (outflow) => outflow.insumoId === rowInsumo.id,
    );

    if (isConfirmed) {
      Swal.fire({
        title: 'Actualizando...',
        text: `Por favor espere mientras se actualiza el insumo "${rowInsumo.nombreInsumo}". Con Nueva Cantidad Retirada: "${this.totalQuantityToRemove}"`,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      this.salesService
        .updateInsumosAdicionales(
          this.dialogData.EDIT_FILE_OUTFLOWS?.file?.id ?? '',
          foodOutflowToUpdate,
        )
        .pipe(takeUntil(this._unsubscribeOnDestroy$))
        .subscribe({
          next: async (res) => {
            try {
              if (!res.updateInsumosAdicionales) {
                Swal.close();
                return;
              }
              if (selectedInsumo) {
                selectedInsumo.cantidad = foodOutflowToUpdate.cantidad;
              }

              const updatedOutflow = await lastValueFrom(
                this.aybService.mutationUpdateStockOutflowQuantity({
                  egresoInsumoId:
                    this.outflowsByService.find(
                      (outflow) => outflow.insumoId === rowInsumo.id,
                    )?.id ?? '',
                  quantity: this.totalQuantityToRemove,
                }),
              );

              this._updateFrontendQuantity(rowInsumo);

              Swal.close();
              this.notificationService.showSuccessAlert(
                `Se actualizó la cantidad del insumo correctamente. Cantidad egresada (confirmada): ${this.totalQuantityToRemove}.`,
              );
              this.cdRef.markForCheck();
            } catch (error) {
              Swal.close();
              console.error('Error al actualizar cantidad de egreso:', error);
              this.notificationService.notifyError(
                'Error al actualizar la cantidad del egreso',
              );
              this.cdRef.markForCheck();
            }
          },
          error: (err) => {
            Swal.close();
            console.error('Error al actualizar insumo adicional:', err);
            this.notificationService.notifyError(
              'Error al actualizar insumo adicional',
            );
            this.cdRef.markForCheck();
          },
        });
    } else {
      if (selectedInsumo) {
        selectedInsumo.cantidad = foodOutflowToUpdate.cantidad;
      }
      this._updateFrontendQuantity(rowInsumo);
      this.notificationService.showSuccessAlert(
        `Se actualizó la cantidad del insumo a retirar en la vista. Confirme los insumos para guardar los cambios.`,
      );
      this.cdRef.markForCheck();
    }
  }

  private _updateFrontendQuantity(rowInsumo: GroupedFoodItemOutflow) {
    const insumoIndex = this.dataSource.data.findIndex(
      (insumo) => insumo.id === rowInsumo.id,
    );
    if (insumoIndex !== -1) {
      this.dataSource.data[insumoIndex].cantidadTotal =
        this.totalQuantityToRemove;
    }

    const insumosIndex = this.insumos.findIndex(
      (insumo) => insumo.id === rowInsumo.id,
    );
    if (insumosIndex !== -1) {
      this.insumos[insumosIndex].cantidadTotal = this.totalQuantityToRemove;
    }

    this.dataSource.data = [...this.dataSource.data];
  }

  setActiveTextArea(rowInsumo: GroupedFoodItemOutflow) {
    this.totalQuantityToRemove = rowInsumo.cantidadTotal || 0;
    this.activeTextArea = rowInsumo.id ?? null;
    this.cdRef.markForCheck();
  }

  onCloseDialog() {
    this.dialogRef.close({
      isthereChanges: this.isthereChanges,
      success: this.success,
      pdf: this.pdf,
      confirmInsumosAYB: this.isInsumosConfirmed,
    });
  }

  private async _extractFoodItemsFromDishes(dishes: DishModel[]) {
    const foodItems: GroupedFoodItemOutflow[] = [];

    await Promise.all(
      dishes.map(async (dishByMenu) => {
        const dish = (
          await lastValueFrom(
            this.aybService
              .queryGetDishById({
                dishId: dishByMenu.id ?? '',
              })
              .pipe(takeUntil(this._unsubscribeOnDestroy$)),
          )
        ).getDishById;

        dish.insumos?.forEach((insumo) => {
          foodItems.push({
            ...insumo,
            // Cantidad x Plato
            cantidad: insumo.cantidad || 0,
            nombrePlato: dish?.nombrePlato,
            nombreMenu: this.selectedMenu,
            service: this.selectedService,
            unidadMedida: insumo.unidadMedida as 'KG' | 'LT' | 'UN',
          });
        });
      }),
    );
    return foodItems;
  }

  private async _getWarehouseSupplies() {
    const res = await firstValueFrom(this.aybService.queryGetAllFoodItemsSku());
    return res?.getAllFoodItemsSku?.insumos || [];
  }

  private async _getExistingOutflowsByMenuByServiceByFile() {
    const outflowByFileIdResponse = await lastValueFrom(
      this.aybService.queryGetAllEgresosInsumosByFileId({
        fileId: this.dialogData.EDIT_FILE_OUTFLOWS.file?.id ?? '',
      }),
    );

    const rawOutflows = outflowByFileIdResponse.getAllEgresosInsumosByFileId;
    
    // Deduplicate outflows keeping the most recent
    const deduplicatedOutflowsMap = new Map<string, FoodItemOutflowModel>();
    rawOutflows.forEach((outflow) => {
      const key = `${outflow.fileId}|${outflow.IdService}|${outflow.menuName}|${outflow.day}|${outflow.insumoId}`;
      const existing = deduplicatedOutflowsMap.get(key);

      if (!existing) {
        deduplicatedOutflowsMap.set(key, outflow);
      } else {
        const existingDate = new Date(existing.updatedAt || existing.createdAt || 0).getTime();
        const currentDate = new Date(outflow.updatedAt || outflow.createdAt || 0).getTime();

        if (currentDate > existingDate) {
          deduplicatedOutflowsMap.set(key, outflow);
        }
      }
    });

    const outflowsByFileId = Array.from(deduplicatedOutflowsMap.values()).map((outflow) => ({
      ...outflow,
      hasDocument: outflow?.hasDocument ?? false,
      observation: outflow?.observation ?? '',
    }));

    this.outflowsByService = outflowsByFileId.filter(
      (outflow) =>
        this.selectedMenu === outflow.menuName &&
        this.dialogData.EDIT_FILE_OUTFLOWS.currentService?.nombreServicio ===
        outflow.serviceName &&
        (!(
          this.dialogData.EDIT_FILE_OUTFLOWS.currentService?.day && outflow.day
        ) ||
          this.dialogData.EDIT_FILE_OUTFLOWS.currentService.day ===
          outflow.day),
    );

    return this.outflowsByService;
  }

  private _outflowsThatCanBeRemoved({
    foodItemsFromDishes,
    serviceExistingOutflows,
  }: {
    serviceExistingOutflows: FoodItemOutflowModel[];
    foodItemsFromDishes: GroupedFoodItemOutflow[];
  }): GroupedFoodItemOutflow[] {
    const removableFoodItems: GroupedFoodItemOutflow[] = [];

    serviceExistingOutflows.forEach((serviceOutflow) => {
      const foodItem = this.warehouseSupplies.find(
        (supply) => supply.id === serviceOutflow.insumoId,
      );

      if (foodItem) {
        const dishQuantity =
          foodItemsFromDishes.find(
            (foodItemFromDish) => foodItemFromDish.id === foodItem.id,
          )?.cantidad || 0;

        removableFoodItems.push({
          ...foodItem,
          nombrePlato: null,
          nombreMenu: this.selectedMenu,
          // Cantidad x Plato
          cantidad: dishQuantity, // Keep the dish quantity for reference
          // Cantidad Egresado
          cantidadTotal: serviceOutflow.cantidad, // Use outflow quantity as total
          service: this.dialogData.EDIT_FILE_OUTFLOWS.currentService,
          unidadMedida: foodItem.unidadMedida as 'KG' | 'LT' | 'UN',
          categoria: foodItem.categoria ?? '',
        } as GroupedFoodItemOutflow);
      }
    });

    return removableFoodItems;
  }

  /**
   * Merges food items from dishes with removable food items
   */
  private _mergeFoodItems({
    foodItemsFromDishes,
    removableFoodItems,
  }: {
    foodItemsFromDishes: GroupedFoodItemOutflow[];
    removableFoodItems: GroupedFoodItemOutflow[];
  }): GroupedFoodItemOutflow[] {
    return [
      ...foodItemsFromDishes.filter(
        (insumo) =>
          !removableFoodItems.some(
            (removableInsumo) => removableInsumo.id === insumo.id,
          ),
      ),
      ...removableFoodItems,
    ];
  }

  private _groupFoodItemsByQuantity({
    foodItems,
  }: {
    foodItems: GroupedFoodItemOutflow[];
  }): GroupedFoodItemOutflow[] {
    const foodItemsGrouped: FoodItemQuantityGroup = {};

    foodItems.forEach((insumo) => {
      if (!insumo?.id) return;

      if (foodItemsGrouped[insumo.id]) {
        // Check if this food item already has an outflow (cantidadTotal is set)
        const existingItem = foodItemsGrouped[insumo.id];
        const isExistingOutflow = existingItem.cantidadTotal != null;

        if (isExistingOutflow) {
          // Only sum the dish quantities for reference
          existingItem.cantidad =
            (existingItem.cantidad || 0) + (insumo.cantidad || 0);
        } else {
          // If no existing outflow, sum both cantidad and cantidadTotal
          existingItem.cantidad =
            (existingItem.cantidad || 0) + (insumo.cantidad || 0);
          existingItem.cantidadTotal =
            (existingItem.cantidadTotal || 0) + (insumo.cantidadTotal || 0);
        }
      } else {
        foodItemsGrouped[insumo.id] = { ...insumo };
      }
    });

    return Object.values(foodItemsGrouped);
  }

  private _addAdditionalFoodSupplies({
    groupedFoodItems,
  }: {
    groupedFoodItems: GroupedFoodItemOutflow[];
  }): void {
    this.dialogData.EDIT_FILE_OUTFLOWS.insumos?.forEach((foodItem) => {
      const existingFoodSupply = groupedFoodItems.find(
        (groupedFoodItem) => groupedFoodItem.id === foodItem.id,
      );

      if (existingFoodSupply) {
        existingFoodSupply.cantidad = foodItem.cantidad;
      } else {
        groupedFoodItems.push({
          ...foodItem,
          nombreMenu: foodItem.menu?.nombreMenu,
        });
      }
    });
  }

  private async _calculateTotalQuantities({
    groupedFoodItems,
  }: {
    groupedFoodItems: GroupedFoodItemOutflow[];
  }): Promise<ProcessedFoodItem[]> {
    return Promise.all(
      groupedFoodItems.map(async (insumo) => {
        const existingOutflow = this.outflowsByService.find(
          (outflow) => outflow.insumoId === insumo.id,
        );

        let cantidadCalculada: number;

        if (existingOutflow && existingOutflow?.cantidad != null) {
          cantidadCalculada = existingOutflow.cantidad;
        } else {
          cantidadCalculada = this._calculateQuantityForItem(insumo);
          if (insumo.unidadMedida === 'UN') {
            cantidadCalculada = this._foodItemQuantityByMenuCount(insumo);
          }
        }

        return {
          ...insumo,
          cantidadTotal: cantidadCalculada,
          cantidadCalculada,
        };
      }),
    );
  }

  private _calculateQuantityForItem(insumo: GroupedFoodItemOutflow): number {
    const baseQuantity = insumo?.cantidad ?? 0;
    const multiplier =
      this.totalMenuCount === 0
        ? this.dialogData.EDIT_FILE_OUTFLOWS.file.lstPaX.length
        : this.totalMenuCount;

    return baseQuantity * multiplier;
  }

  private _foodItemQuantityByMenuCount(
    foodItem: GroupedFoodItemOutflow,
  ): number {
    const menuCount =
      this.totalMenuCount ||
      this.dialogData.EDIT_FILE_OUTFLOWS.file.lstPaX.length;

    return Math.ceil(menuCount / (1 / (foodItem?.cantidad || 1)));
  }

  private _applyExistingOutflowData({
    existingOutflows,
    processedFoodItems,
  }: {
    processedFoodItems: ProcessedFoodItem[];
    existingOutflows: FoodItemOutflowModel[];
  }): void {
    existingOutflows.forEach((outflow) => {
      const selectedFoodItem = processedFoodItems.find(
        (foodItem) => foodItem.id === outflow.insumoId,
      );

      if (selectedFoodItem) {
        selectedFoodItem.checked = true;
        selectedFoodItem.observation = outflow.observation || '';
        selectedFoodItem.hasDocument = !!outflow.hasDocument;
      }
    });
  }

  private _sortFoodItems({
    foodItems,
  }: {
    foodItems: ProcessedFoodItem[];
  }): GroupedFoodItemOutflow[] {
    const foodItemsWithCode = foodItems.map((foodItem) => {
      const warehouseItem = this.warehouseSupplies.find(
        (supply) => supply.id === foodItem.id,
      );
      return {
        ...foodItem,
        codigoInsumo: warehouseItem?.codigoInsumo,
      };
    });

    return foodItemsWithCode.sort((foodItemA, foodItemB) => {
      if (foodItemA.checked && !foodItemB.checked) {
        return -1;
      } else if (!foodItemA.checked && foodItemB.checked) {
        return 1;
      } else {
        // Sort by codigoInsumo
        const codeA = foodItemA.codigoInsumo || '';
        const codeB = foodItemB.codigoInsumo || '';
        return codeA.localeCompare(codeB);
      }
    });
  }

  // private _filterWarehouseSupplies (
  //   sortedFoodItems: GroupedFoodItemOutflow[]
  // ): void {
  //   this.warehouseSupplies = this.warehouseSupplies.filter(
  //     warehouseFoodItem => {
  //       return sortedFoodItems.some(
  //         foodItem => foodItem.id === warehouseFoodItem.id
  //       )
  //     }
  //   )
  // }

  private _updateComponentState({
    sortedFoodItems,
  }: {
    sortedFoodItems: GroupedFoodItemOutflow[];
  }): void {
    this.insumos = sortedFoodItems;
    this.dataSource.data = sortedFoodItems;
    this._extractAvailableCategories();
    this.applyFilterSelect();
    this.isLoading = false;
    this.cdRef.markForCheck();
  }

  private _extractAvailableCategories(): void {
    const categoriesSet = new Set<string>();

    this.insumos.forEach((insumo) => {
      if (insumo.codigoInsumo) {
        const match = insumo.codigoInsumo.match(/^[A-Z]+/);
        if (match) {
          categoriesSet.add(match[0]);
        }
      }
    });

    this.availableCategories = Array.from(categoriesSet).sort();
  }
}
